import React from 'react';
import { ILandingPageProps } from '../../interfaces/IPages';
import { IShip } from '../../interfaces/ship/IShip';
import { IconList } from '../components/atoms/icon-list/icon-list';
import { ILabelIconProps } from '../components/atoms/label-icon/label-icon';
import {
    CheckMarkIcon,
    StyledHighlightInnerWrapper,
} from '../components/sections/highlights/highlights.style';
import { IImage, Overview } from '../components/sections/overview/overview';
import { theme } from '../style/global-style';
import { getTranslatedMonth } from '../utils/date';
import { getDetectedDevice } from '../utils/device-detection';
import { __ } from '../utils/translation-service';

interface IProps {
    text: string;
    icons: ILabelIconProps[];
    images: IImage[];
}

export const OverviewMod = ({ getLandingPage, modulesData }: ILandingPageProps['pageProps']) => {
    const data = modulesData[`${getLandingPage.type}${getLandingPage.id}`];
    let props: IProps = {} as any;
    switch (getLandingPage.type) {
        case 'ship': {
            props = shipData(data as IShip);
            break;
        }
        // we don't use below cases like 'destination' and 'cruiseline' maybe we should remove them
        case 'destination': {
            props = destinationData(data, modulesData);
            break;
        }
        case 'cruiseline': {
            props = cruiselineData(data);
            break;
        }
    }

    const title = `${getLandingPage?.title ? getLandingPage.title : ''}`;

    if (!data || !data.highlights) {
        return null;
    }

    const labels = groupLabels(data.highlights);

    const highlights = labels?.length
        ? [
              <StyledHighlightInnerWrapper key="highlights-list">
                  <IconList
                      color={theme.labels.onlineBookable}
                      labels={labels}
                      icon={<CheckMarkIcon />}
                  />
              </StyledHighlightInnerWrapper>,
          ]
        : [];

    const isDesktop = getDetectedDevice() === 'desktop';
    const subHeadline1 = isDesktop
        ? `${__('Cruises with %s (ship page)').replace('%s', title)}`
        : `${title} - ${__('Ship impression')}`;

    return (
        <>
            <Overview
                key="overview"
                title={title}
                headline={`${__('overviewHeadline')}`}
                subHeadline1={subHeadline1}
                buttonLabel={__('overviewButton')}
                descriptionPoints={data?.descriptionPoints ? data.descriptionPoints : null}
                highlights={highlights}
                description={data.seoText}
                {...props}
            />
        </>
    );
};

const shipData = (data: IShip): IProps => {
    const text: string = data?.teaserDescription ? data.teaserDescription : '';
    let icons: any = [];
    let images: any = [];
    if (data?.factsAndFigures) {
        const { manufactureYear, passengerCapacity, crewMembers, decks } = data.factsAndFigures;
        if (manufactureYear) {
            icons.push({
                type: 'buildYear',
                amount: manufactureYear,
            });
        }
        if (passengerCapacity) {
            icons.push({
                type: 'passenger',
                amount: passengerCapacity,
            });
        }
        if (crewMembers) {
            icons.push({
                type: 'crew',
                amount: crewMembers,
            });
        }
        if (decks) {
            icons.push({ type: 'decks', amount: decks });
        }
        if (data?.images?.length) {
            images = data.images.map((item) => ({
                src: item.image.url,
                alt: data.title,
                placeholder: item.thumb.url,
                width: item.image.width,
                height: item.image.height,
            }));
            images = images.slice(0, 10);
        }
    }

    return { text, images, icons };
};

const destinationData = (data: any, modulesData: any | undefined): IProps => {
    const text: string = data?.teaserDescription ? data.teaserDescription : '';
    let icons: any = [];
    let images: any = [];
    const cruiseSearch = modulesData['cruiseSearchlandingpageOverview'];
    const routes = cruiseSearch.totalCount ? cruiseSearch.totalCount : null;
    const harbourSearch = modulesData['harbourSearchharbourTeaser'];
    const harbours = harbourSearch?.totalCount;
    const topMonth = harbourSearch?.items[0]?.regions[0]?.topMonth;
    const travelWindow = topMonth ? getTranslatedMonth(topMonth) : null;

    if (harbours) {
        icons.push({ type: 'harbours', amount: harbours });
    }

    if (routes) {
        icons.push({ type: 'routes', amount: routes });
    }

    if (travelWindow) {
        icons.push({
            type: 'travelWindow',
            amount: travelWindow,
        });
    }

    if (data?.images?.length) {
        images = data.images.map((item: any) => ({
            src: item.url[0],
            alt: data.title,
            placeholder: item.url[1],
        }));
        images = images.slice(0, 10);
    }

    return { text, images, icons };
};

const cruiselineData = (data: any): IProps => {
    const text: string = data?.teaserDescription ? data.teaserDescription : '';
    let icons: any = [];
    let images: any = [];
    const ships = data.brand.numberOfShips;
    const boardLanguage = data.boardLanguages ? data.boardLanguages[0] : null;
    const newestShip = data.newestShip ? data.newestShip : null;
    const destinations = data.brand.regionNames ? data.brand.regionNames.length : null;

    if (ships) {
        icons.push({ type: 'ship', amount: ships });
    }

    if (boardLanguage) {
        icons.push({ type: 'language', amount: boardLanguage });
    }

    if (newestShip) {
        icons.push({ type: 'newestShip', amount: newestShip });
    }

    if (destinations) {
        icons.push({
            type: 'destinations',
            amount: destinations,
        });
    }

    if (data?.images?.length) {
        images = data.images.map((item: any) => ({
            src: item.url[0],
            alt: data.title,
            placeholder: item.url[1],
        }));
        images = images.slice(0, 10);
    }

    return { text, images, icons };
};

const groupLabels = (labels: string[]): string[] | null => {
    if (!labels.length || labels.length < 3) return null;
    // Show only first three items in scope of this task DREAM-16359
    return labels.slice(0, 3);
};
